import moment, { Moment } from "moment";
import type { DealBuilderInputStore } from "../../stores/deal-builder-inputs.store.ts";
import type { SerialBond } from "../pages/debt-service-page.tsx";
import { ExpandedRatingOptions, RatingAssumption, SaleMethod, SecurityType, TaxDesignation, USState } from "../services/utils/primary-constants.ts";
import type { ExtractedDeal, PdfExtractionResponse } from "../api/pdf-extraction-api.ts";

export const DATE_FMT = "YYYY-MM-DD";
export const DEFAULT_COUPON = 0.05;

export type ExtractionPrefill = {
    inputPatch: Partial<DealBuilderInputStore>,
    bonds: SerialBond[],
    termRanges: [Moment, Moment][],
    reviewFields: string[],
    warnings: string[],
};

/**
 * Returns the enum value matching the extracted string, or undefined if
 * the model returned something outside the allowed options.
 */
export function parseEnum<T extends Record<string, string>>(enumObject: T, value: string | null | undefined): T[keyof T] | undefined {
    if (value != null && Object.values(enumObject).includes(value)) return value as T[keyof T];
    return undefined;
}

export function parseDate(value: string | null | undefined): Moment | undefined {
    if (value == null) return undefined;
    const date = moment(value, DATE_FMT, true);
    return date.isValid() ? date : undefined;
}

/**
 * The debt service table buckets bonds by walking back from the final
 * maturity in exact 6 month steps and looking dates up by "YYYY-MM-DD" key,
 * silently dropping any bond outside that grid. Every extracted maturity
 * must land on the grid before we can safely inject the schedule.
 */
export function isOnSemiannualGrid(maturities: Moment[]): boolean {
    const first = maturities[0];
    const final = maturities[maturities.length - 1];

    const grid = new Set<string>();
    const cursor = final.clone();
    while (cursor.isSameOrAfter(first, "day")) {
        grid.add(cursor.format(DATE_FMT));
        cursor.subtract(6, "months");
    }

    return maturities.every(x => grid.has(x.format(DATE_FMT)));
}

/**
 * When the PDF doesn't state the first coupon date, derive it by stepping
 * back from the first maturity in 6 month increments to the first date
 * strictly after the delivery date. This keeps the first coupon and first
 * principal aligned on the semiannual grid the validator expects.
 */
export function deriveFirstCouponDate(firstMaturity: Moment, deliveryDate: Moment): Moment {
    const candidate = firstMaturity.clone();
    while (candidate.clone().subtract(6, "months").isAfter(deliveryDate, "day")) {
        candidate.subtract(6, "months");
    }
    return candidate;
}

/**
 * Maps the backend PDF extraction response into a patch for the deal builder
 * input store plus a bond schedule to inject at Build Deal time. Fields the
 * PDF didn't provide keep their current store value and are reported in
 * `reviewFields` so the user knows to double check them.
 */
export function mapExtractionToPrefill(
    response: PdfExtractionResponse,
    current: DealBuilderInputStore,
): ExtractionPrefill {
    const inputPatch: Partial<DealBuilderInputStore> = {};
    const reviewFields: string[] = [];
    const warnings: string[] = [...(response.warnings ?? [])];
    const deal: Partial<ExtractedDeal> = response.deal ?? {};

    // Report titles
    if (deal.borrower) inputPatch.borrower = deal.borrower;
    else reviewFields.push("Borrower");
    if (deal.issuance_title) inputPatch.issuanceTitle = deal.issuance_title;
    else reviewFields.push("Issuance Title");

    // Enum backed pricing assumptions
    const enumFields: [string, keyof DealBuilderInputStore, Record<string, string>, string | null][] = [
        ["State of Issuance", "stateOfIssuance", USState, deal.state_of_issuance],
        ["Security Type", "securityType", SecurityType, deal.security_type],
        ["Rating Assumption", "ratingAssumption", RatingAssumption, deal.rating_assumption],
        ["Tax Designation", "taxDesignation", TaxDesignation, deal.tax_designation],
        ["Sale Method", "saleMethod", SaleMethod, deal.sale_method],
    ];
    for (const [label, key, enumObject, value] of enumFields) {
        const parsed = parseEnum(enumObject, value);
        if (parsed !== undefined) (inputPatch as any)[key] = parsed;
        else reviewFields.push(label);
    }

    const stateEnhancement = parseEnum(ExpandedRatingOptions, deal.state_enhancement);
    inputPatch.useStateEnhancement = stateEnhancement !== undefined;
    inputPatch.stateEnhancement = stateEnhancement;

    if (deal.bond_insurance !== null && deal.bond_insurance !== undefined) {
        inputPatch.bondInsurance = deal.bond_insurance;
    } else {
        reviewFields.push("Bond Insurance");
    }

    // Amortization assumptions
    const deliveryDate = parseDate(deal.delivery_date);
    if (deliveryDate) inputPatch.deliveryDate = deliveryDate;
    else reviewFields.push("Delivery Date");

    // The market conditions date isn't in the PDF (it defaults to today), but
    // deals from the past would fail validation against today's market, so
    // move it near the delivery date instead.
    if (deliveryDate && deliveryDate.isBefore(moment(), "day")) {
        inputPatch.marketConditionsDate = deliveryDate.clone().subtract(30, "days");
        reviewFields.push("Date of Market Conditions");
    }

    if (deal.denomination) inputPatch.denomination = deal.denomination;
    else reviewFields.push("Denomination");

    const callDate = parseDate(deal.call_date);
    inputPatch.useCallDate = callDate !== undefined;
    if (callDate) inputPatch.callDate = callDate;
    else reviewFields.push("Call Date");

    // Maturity schedule
    const rows = (response.schedule ?? [])
        .map(row => ({ ...row, maturity: parseDate(row.maturity_date) }))
        .filter((row): row is typeof row & { maturity: Moment } => row.maturity !== undefined)
        .sort((a, b) => a.maturity.diff(b.maturity));

    if ((response.schedule?.length ?? 0) > rows.length) {
        warnings.push("Some maturities had invalid dates and were skipped.");
    }

    let bonds: SerialBond[] = [];
    let termRanges: [Moment, Moment][] = [];

    if (rows.length > 0 && !isOnSemiannualGrid(rows.map(x => x.maturity))) {
        warnings.push("The extracted maturities are not on a regular semiannual grid, so the schedule was not imported.");
    } else if (rows.length > 0) {
        bonds = rows.map(row => ({
            type: "serial",
            maturity: row.maturity.toISOString(),
            principal: row.principal ?? 0,
            coupon: row.coupon ?? DEFAULT_COUPON,
            lockedPrincipal: row.principal !== null,
            lockedCoupon: row.coupon !== null,
        }));

        // Rows sharing a term_bond_maturity are the sinking fund payments of
        // one term bond. They are injected as serial bonds and regrouped
        // after the deal is built.
        const termGroups = new Map<string, Moment[]>();
        for (const row of rows) {
            if (!row.term_bond_maturity) continue;
            const group = termGroups.get(row.term_bond_maturity) ?? [];
            group.push(row.maturity);
            termGroups.set(row.term_bond_maturity, group);
        }
        termRanges = [...termGroups.values()]
            .filter(group => group.length > 1)
            .map(group => [group[0].clone(), group[group.length - 1].clone()]);

        // Dates derived from the schedule win over the scalar fields.
        inputPatch.firstPrincipalDate = rows[0].maturity.clone();
        inputPatch.finalPrincipalDate = rows[rows.length - 1].maturity.clone();

        const semiannual = rows.some((row, i) =>
            i > 0 && rows[i].maturity.diff(rows[i - 1].maturity, "months", true) < 7);
        inputPatch.principalRate = semiannual ? 2 : (deal.principal_frequency || 1);
    } else if (deal.principal_frequency) {
        inputPatch.principalRate = deal.principal_frequency;
    }

    // Par amount: trust the schedule when it's complete, otherwise fall back
    // to the stated total.
    const principals = rows.map(x => x.principal);
    if (principals.length > 0 && principals.every(x => x !== null)) {
        inputPatch.parAmount = (principals as number[]).reduce((a, b) => a + b, 0);
    } else if (deal.par_amount) {
        inputPatch.parAmount = deal.par_amount;
    } else {
        reviewFields.push("Par Amount");
    }

    const parAmount = inputPatch.parAmount ?? current.parAmount;
    const denomination = inputPatch.denomination ?? current.denomination;
    if (parAmount % denomination !== 0) {
        warnings.push("The extracted par amount is not divisible by the denomination.");
    }

    // First coupon date: prefer the stated one, otherwise derive it from the
    // first maturity so it stays aligned with the principal grid.
    const firstCouponDate = parseDate(deal.first_coupon_date);
    if (firstCouponDate) {
        inputPatch.firstCouponDate = firstCouponDate;
    } else if (inputPatch.firstPrincipalDate) {
        const effectiveDelivery = deliveryDate ?? current.deliveryDate;
        inputPatch.firstCouponDate = deriveFirstCouponDate(inputPatch.firstPrincipalDate, effectiveDelivery);
        reviewFields.push("First Coupon Date");
    } else {
        reviewFields.push("First Coupon Date");
    }

    return { inputPatch, bonds, termRanges, reviewFields, warnings };
}
